// Shading for the skin editor: the Lighten / Darken / Noise brushes and the shade palette.
// Pure — no React, no DOM — so `scripts/skin-editor.selftest.ts` runs it under plain Node.
//
// WHY OKLab: a lightness step of the same size LOOKS the same size on yellow and on blue (HSL's
// lightness does not — +10% on pure yellow is barely visible, on pure blue it is a jump). Every
// operation here changes OKLab lightness L and keeps the hue angle: when the new lightness would put
// the color outside sRGB, the CHROMA is reduced (hue unchanged) until it fits, instead of clamping
// each channel, which shifts the hue.
//
// WHY the brushes read the STROKE'S STARTING pixel: see `paintStroke` in edits.ts. A pixel's result
// depends only on (its pixel when the stroke began, x, y, stroke seed, strength), so dragging back
// over a pixel in the same stroke gives the SAME result — the stroke cannot keep darkening or keep
// re-rolling noise just because the pointer wiggled. A new stroke (new seed, new starting pixels)
// builds on the last one, which is how dodge/burn is expected to accumulate.

import type { Color } from './types';

export interface OkLab { L: number; a: number; b: number }

const toLinear = (c: number) => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};
const fromLinear = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055);

export function colorToOkLab(c: Color): OkLab {
  const r = toLinear(c.r), g = toLinear(c.g), b = toLinear(c.b);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return {
    L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

/** Linear-light sRGB (unclamped) of an OKLab color. */
function okLabToLinear({ L, a, b }: OkLab): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const EPS = 1e-6;
const inGamut = (rgb: [number, number, number]) => rgb.every(v => v >= -EPS && v <= 1 + EPS);

/**
 * `c` with its OKLab lightness set to `L` (clamped to 0..1), hue kept. Out of gamut → the chroma is
 * scaled down (binary search) until it fits. Alpha is kept. Always returns whole 0..255 channels.
 */
export function withLightness(c: Color, L: number): Color {
  const lab = colorToOkLab(c);
  const target = Math.min(1, Math.max(0, L));
  let lo = 0, hi = 1;
  if (inGamut(okLabToLinear({ L: target, a: lab.a, b: lab.b }))) lo = 1;
  else {
    for (let i = 0; i < 20; i++) {
      const k = (lo + hi) / 2;
      if (inGamut(okLabToLinear({ L: target, a: lab.a * k, b: lab.b * k }))) lo = k; else hi = k;
    }
  }
  const [r, g, b] = okLabToLinear({ L: target, a: lab.a * lo, b: lab.b * lo });
  const ch = (v: number) => Math.round(Math.min(1, Math.max(0, fromLinear(Math.min(1, Math.max(0, v))))) * 255);
  return { r: ch(r), g: ch(g), b: ch(b), a: c.a };
}

// ---------------------------------------------------------------------------------------------
// Brushes. `strength` is 0..1 in OKLab lightness units (0.1 = "10%"). A fully transparent pixel is
// never touched: shading only ever changes pixels that are already painted.
// ---------------------------------------------------------------------------------------------

export function lightenPixel(src: Color, strength: number): Color {
  if (src.a === 0) return src;
  return withLightness(src, colorToOkLab(src).L + Math.max(0, strength));
}

export function darkenPixel(src: Color, strength: number): Color {
  if (src.a === 0) return src;
  return withLightness(src, colorToOkLab(src).L - Math.max(0, strength));
}

/**
 * A number in [-1, 1] from (x, y, seed) — a fixed integer hash (no Math.random), so the same pixel in
 * the same stroke always gets the same value and a failing self-test reproduces.
 */
export function hashNoise(x: number, y: number, seed: number): number {
  let h = (Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(seed | 0, 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return (h / 0xffffffff) * 2 - 1;
}

/** Lightness moved by a seeded random amount in [-strength, +strength]; hue and alpha kept. */
export function noisePixel(src: Color, x: number, y: number, seed: number, strength: number): Color {
  if (src.a === 0) return src;
  return withLightness(src, colorToOkLab(src).L + hashNoise(x, y, seed) * Math.max(0, strength));
}

// ---------------------------------------------------------------------------------------------
// Shade palette
// ---------------------------------------------------------------------------------------------

/** Largest lightness step between neighboring shades (OKLab L). */
export const SHADE_STEP = 0.08;

/**
 * Up to `steps` darker shades, the color itself, up to `steps` lighter shades — ordered darkest →
 * lightest, every swatch a DIFFERENT color. Steps are equal in OKLab lightness (SHADE_STEP), compressed
 * near black or white so the ends do not overshoot. Where there is no room left (pure black has nothing
 * darker, pure white nothing lighter, and near them 8-bit rounding lands neighbors on the same value)
 * the repeats are dropped, so the list can be shorter than 2·steps+1 and the color is not always in the
 * middle: find it with `shadeIndex`. Hue and alpha are kept.
 */
export function shadePalette(color: Color, steps = 3): Color[] {
  const L = colorToOkLab(color).L;
  const down = Math.min(SHADE_STEP, L / (steps + 1));
  const up = Math.min(SHADE_STEP, (1 - L) / (steps + 1));
  const ramp: Color[] = [];
  for (let i = steps; i >= 1; i--) ramp.push(withLightness(color, L - i * down));
  ramp.push({ ...color });
  for (let i = 1; i <= steps; i++) ramp.push(withLightness(color, L + i * up));
  // Lightness is monotonic along the ramp, so equal colors can only be NEIGHBORS; keep the color itself
  // (never a shade that rounded onto it) and the first of every other run.
  const out: Color[] = [];
  for (let i = 0; i < ramp.length; i++) {
    const c = ramp[i];
    const prev = out[out.length - 1];
    if (prev && sameColor(prev, c)) {
      if (i === steps) out[out.length - 1] = c; // the color itself replaces a shade equal to it
      continue;
    }
    out.push(c);
  }
  return out;
}

function sameColor(a: Color, b: Color): boolean {
  return a.r === b.r && a.g === b.g && a.b === b.b && a.a === b.a;
}

/** Where `color` sits in its `shadePalette` (darker swatches before it, lighter after). */
export function shadeIndex(palette: readonly Color[], color: Color): number {
  return palette.findIndex(c => sameColor(c, color));
}
